import { useState } from "react";
import { Check, Pencil, RotateCcw, X } from "lucide-react";
import { Modal } from "../Modal";
import { Pairs, RecordSheet, SectionBar } from "../RecordSections";
import { formatSchoolDate } from "../students/studentDisplay";
import { apiErrorMessage } from "../../utils/apiError";
import type { AllowanceLine, SalaryChangeRequest } from "../../services/staffService";
import {
  PAY_CHANGE_STATUS_DOTS,
  PAY_CHANGE_STATUS_LABELS,
  grossChange,
} from "../../pages/payChangeFilters";
import { describeChange } from "./PayChangeModal";

type Decision = "return" | "reject";

type Props = {
  change: SalaryChangeRequest;
  canDecide: boolean;
  canRevise: boolean;
  busy: boolean;
  error: unknown;
  onApprove: () => void;
  onReturn: (reason: string) => void;
  onReject: (reason: string) => void;
  onRevise: () => void;
  onClose: () => void;
};

export function PayChangeStatus({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
      <span className={`h-1.5 w-1.5 rounded-full ${PAY_CHANGE_STATUS_DOTS[status] || "bg-gray-400"}`} />
      {PAY_CHANGE_STATUS_LABELS[status] || status.replace(/_/g, " ")}
    </span>
  );
}

/** Every allowance on either side, so dropped and new ones both show. */
function allowanceRows(before: AllowanceLine[] = [], after: AllowanceLine[] = []) {
  const names = [...new Set([...before, ...after].map((line) => line.name))];
  const amount = (lines: AllowanceLine[], name: string) =>
    Number(lines.find((line) => line.name === name)?.amount ?? 0);
  return names.map((name) => [name, amount(before, name), amount(after, name)] as const);
}

export default function PayChangeReviewModal({
  change,
  canDecide,
  canRevise,
  busy,
  error,
  onApprove,
  onReturn,
  onReject,
  onRevise,
  onClose,
}: Props) {
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState("");
  const gross = grossChange(change);
  const awaiting = canDecide && change.workflow_status === "submitted";
  const money = (amount: number) => amount.toLocaleString();
  const rows: [string, number, number, boolean?][] = [
    ["Basic pay", Number(change.previous_salary || 0), Number(change.proposed_salary || 0)],
    ...allowanceRows(change.previous_allowances, change.proposed_allowances).map(
      ([name, before, after]) => [`  ${name}`, before, after] as [string, number, number],
    ),
    ["Gross pay", gross.before, gross.after, true],
  ];
  const outcome = change.return_reason || change.rejection_reason || change.review_comments;

  return (
    <Modal
      isOpen
      size="3xl"
      title="Pay change request"
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        decision ? (
          <>
            <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setDecision(null)}>
              Back
            </button>
            <button
              type="submit"
              form="pay-change-decision"
              disabled={busy || reason.trim().length < 5}
              className={`btn gap-2 text-white ${decision === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}
            >
              {decision === "reject" ? <X className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
              {decision === "reject" ? "Reject request" : "Return for changes"}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-outline" disabled={busy} onClick={onClose}>
              Close
            </button>
            {canRevise && (
              <button type="button" className="btn btn-primary gap-2" onClick={onRevise}>
                <Pencil className="h-4 w-4" /> Revise &amp; resubmit
              </button>
            )}
            {awaiting && (
              <>
                <button type="button" className="btn btn-outline gap-2 text-red-700" disabled={busy} onClick={() => setDecision("reject")}>
                  <X className="h-4 w-4" /> Reject
                </button>
                <button type="button" className="btn btn-outline gap-2 text-amber-700" disabled={busy} onClick={() => setDecision("return")}>
                  <RotateCcw className="h-4 w-4" /> Return
                </button>
                <button type="button" className="btn gap-2 bg-green-600 text-white hover:bg-green-700" disabled={busy} onClick={onApprove}>
                  <Check className="h-4 w-4" /> {busy ? "Approving…" : "Approve"}
                </button>
              </>
            )}
          </>
        )
      }
    >
      <div className="space-y-4">
        {Boolean(error) && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            {apiErrorMessage(error, "The decision could not be saved. Please try again.")}
          </p>
        )}

        <RecordSheet>
          <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold leading-6 text-gray-900">{change.staff_name}</h2>
              <p className="text-xs text-gray-500">
                Effective {formatSchoolDate(change.effective_date)} · requested by {change.initiated_by_name}
                {change.created_at && ` on ${formatSchoolDate(change.created_at)}`}
              </p>
            </div>
            <div className="text-right">
              <PayChangeStatus status={change.workflow_status} />
              <p
                className={`text-sm font-semibold tabular-nums ${gross.difference > 0 ? "text-green-700" : gross.difference < 0 ? "text-red-700" : "text-gray-500"}`}
              >
                {describeChange(gross.before, gross.after)}
              </p>
            </div>
          </header>

          <SectionBar letter="A" title={`Pay (${change.currency})`} />
          <div className="px-4 py-3">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="py-1 text-left font-semibold"></th>
                  <th className="py-1 text-right font-semibold">Current</th>
                  <th className="py-1 text-right font-semibold">Proposed</th>
                  <th className="py-1 text-right font-semibold">Change</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, before, after, total]) => (
                  <tr key={label} className={total ? "border-t border-gray-200 font-semibold" : ""}>
                    <td className={`py-1 ${label.startsWith("  ") ? "pl-4 text-gray-500" : "text-gray-700"}`}>
                      {label.trim()}
                    </td>
                    <td className="py-1 text-right text-gray-600">{money(before)}</td>
                    <td className="py-1 text-right text-gray-900">{money(after)}</td>
                    <td
                      className={`py-1 text-right ${after > before ? "text-green-700" : after < before ? "text-red-700" : "text-gray-400"}`}
                    >
                      {describeChange(before, after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SectionBar letter="B" title="Request" />
          <Pairs
            rows={[
              ["Reason", change.reason],
              ["Requested by", change.initiated_by_name],
              ["Effective", formatSchoolDate(change.effective_date)],
              ["Decided by", change.approved_by_name],
            ]}
          />

          {outcome && (
            <div
              className={`flex gap-4 border-t px-4 py-2 ${change.workflow_status === "rejected" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}
            >
              <span className="whitespace-nowrap text-gray-600">
                {change.workflow_status === "rejected" ? "Rejected:" : change.return_reason ? "Returned:" : "Comments:"}
              </span>
              <p className="text-gray-900">{outcome}</p>
            </div>
          )}
        </RecordSheet>

        {decision && (
          <form
            id="pay-change-decision"
            onSubmit={(event) => {
              event.preventDefault();
              const text = reason.trim();
              if (text.length < 5) return;
              if (decision === "reject") onReject(text);
              else onReturn(text);
            }}
          >
            <label className="block text-sm font-medium text-gray-700">
              {decision === "reject" ? "Why is this rejected? *" : "What needs to change? *"}
              <textarea
                autoFocus
                required
                minLength={5}
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                className="input mt-1 h-auto"
                placeholder={
                  decision === "reject"
                    ? "e.g. Not within this year's pay budget."
                    : "e.g. Effective date should be the start of next term."
                }
              />
              <span className="mt-1 block text-xs font-normal text-gray-500">
                {decision === "reject"
                  ? "The request is closed. A new one can be raised later."
                  : `${change.initiated_by_name} can revise it and resubmit.`}
              </span>
            </label>
          </form>
        )}
      </div>
    </Modal>
  );
}
